import { useQuery } from "@tanstack/react-query";
import { Download, Inbox, Mail } from "lucide-react";
import { PageHeader, Panel } from "@/components/dashboard/primitives";
import { Button } from "@/components/ui/button";
import Loading from "@/components/dashboard/Loading";
import api from "@/lib/api";
import { toast } from "sonner";

function toCSV(rows) {
  const headers = ["Full Name", "Work Email", "Company", "Building", "Agent Count", "Source", "Submitted"];
  const keys = ["full_name", "work_email", "company", "building", "agent_count", "source", "created_at"];
  const escape = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.join(",")];
  rows.forEach((r) => lines.push(keys.map((k) => escape(r[k])).join(",")));
  return lines.join("\n");
}

export default function WaitlistInbox() {
  const { data, isLoading, error } = useQuery({
    queryKey: ["waitlist"],
    queryFn: async () => (await api.get("/waitlist")).data,
    retry: false,
  });

  const download = () => {
    const csv = toCSV(data || []);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `vora-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("CSV exported");
  };

  if (isLoading) {
    return (<div><PageHeader title="Waitlist Inbox" subtitle="Early access and developer signups." showDemo={false} /><Loading /></div>);
  }

  if (error) {
    return (
      <div>
        <PageHeader title="Waitlist Inbox" subtitle="Early access and developer signups." showDemo={false} />
        <Panel className="p-12 text-center">
          <p className="text-white font-medium">Admin access required</p>
          <p className="mt-1 text-sm text-slate-400">Only the Vvvoraa admin account can view waitlist submissions.</p>
        </Panel>
      </div>
    );
  }

  const rows = data || [];

  return (
    <div>
      <PageHeader title="Waitlist Inbox" subtitle={`${rows.length} signup${rows.length === 1 ? "" : "s"} for early access.`} showDemo={false}>
        <Button onClick={download} disabled={!rows.length} className="bg-[#4F8CFF] hover:bg-[#3f7af0] text-white rounded-full gap-1.5" data-testid="waitlist-export-btn">
          <Download className="h-4 w-4" /> Export CSV
        </Button>
      </PageHeader>

      {rows.length === 0 ? (
        <Panel className="p-12 text-center" data-testid="waitlist-empty">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#4F8CFF]/12 text-[#4F8CFF]"><Inbox className="h-7 w-7" /></div>
          <p className="mt-4 text-white font-medium">No signups yet</p>
          <p className="mt-1 text-sm text-slate-400">Early access submissions will appear here.</p>
        </Panel>
      ) : (
        <>
          <Panel className="hidden md:block overflow-hidden" data-testid="waitlist-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/8 text-left text-xs font-mono uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3.5 font-medium">Name</th>
                  <th className="px-5 py-3.5 font-medium">Email</th>
                  <th className="px-5 py-3.5 font-medium">Company</th>
                  <th className="px-5 py-3.5 font-medium">Agents</th>
                  <th className="px-5 py-3.5 font-medium">Source</th>
                  <th className="px-5 py-3.5 font-medium">Submitted</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 last:border-0 hover:bg-white/[0.03] transition-colors" data-testid={`waitlist-row-${r.id}`}>
                    <td className="px-5 py-4 text-white">{r.full_name}</td>
                    <td className="px-5 py-4 text-slate-300">{r.work_email}</td>
                    <td className="px-5 py-4 text-slate-400">{r.company || "—"}</td>
                    <td className="px-5 py-4 text-slate-400">{r.agent_count || "—"}</td>
                    <td className="px-5 py-4"><span className="text-[10px] font-mono uppercase tracking-wider rounded-full border border-white/12 px-2 py-0.5 text-slate-400">{r.source}</span></td>
                    <td className="px-5 py-4 text-slate-500 whitespace-nowrap">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>

          <div className="md:hidden space-y-3">
            {rows.map((r) => (
              <Panel key={r.id} className="p-4" data-testid={`waitlist-card-${r.id}`}>
                <p className="text-sm font-semibold text-white">{r.full_name}</p>
                <p className="text-xs text-slate-400 mt-0.5 inline-flex items-center gap-1.5"><Mail className="h-3 w-3" /> {r.work_email}</p>
                <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs text-slate-500">
                  <span>{r.company || "—"}</span>
                  <span>{r.agent_count || "—"} agents</span>
                  <span>{r.source}</span>
                </div>
              </Panel>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
